import React from 'react';
import { Suspense } from 'react';
import { Spinner } from '../../../../components/Spinner/Spinner';
import { useSuspenseQueryAllPages } from '../../../../model/graphql';
import { graphql } from '../../../../generated';
import { FormSelect, Props as FormSelectProps } from '../../../../components/Form/Select/FormSelect';
import { t } from '../../../../locales/translate';

const GET_VENUE_LAYOUTS = graphql(`
  query EventVenueLayoutSelection($id: ID!, $after: String) {
    venue(id: $id) {
      venueLayouts(after: $after, filters: { archived: false, cloned: false }) {
        nodes {
          id
          name
          pricingCategories {
            id
            name
          }
          capacity
          requiresMapSelection
          importStatus
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`);

type Props = FormSelectProps<string> & {
  venueId: string;
};

function VenueLayoutSelect$({ venueId, ...rest }: Props): JSX.Element {
  const { data } = useSuspenseQueryAllPages(GET_VENUE_LAYOUTS, {
    variables: { id: venueId },
    pageInfoExtractor: (data) => data.venue.venueLayouts.pageInfo,
    mergeResults(previousData, fetchMoreResult) {
      return {
        venue: {
          venueLayouts: {
            ...fetchMoreResult.venue.venueLayouts,
            nodes: [...previousData.venue.venueLayouts.nodes, ...fetchMoreResult.venue.venueLayouts.nodes],
          },
        },
      };
    },
  });

  const venueLayoutOptions =
    data?.venue?.venueLayouts?.nodes.map((layout) => {
      const pricingCategory = layout.pricingCategories
        ? `(${layout.pricingCategories.length}) ${layout.pricingCategories.length > 1 ? t.page.event.create.pricingCategory._plural : t.page.event.create.pricingCategory._name}`
        : '';
      const capacity = `(${layout.capacity || 0}) ${t.page.event.create.capacity}`;
      return {
        value: layout.id,
        label: `${layout.name} - ${pricingCategory} - ${capacity}`,
        disabled: layout.importStatus === 'pending' || layout.importStatus === 'error',
      };
    }) || [];

  return <FormSelect {...rest} label={t.page.event.create.venueLayout} options={venueLayoutOptions} allowEmpty />;
}

export function VenueLayoutSelect(props: Props): JSX.Element {
  return (
    <Suspense fallback={<Spinner color="red" />}>
      <VenueLayoutSelect$ {...props} />
    </Suspense>
  );
}
